import {
  FC,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useState,
  useRef,
  Ref,
  forwardRef,
} from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import moment from 'moment';

import { FormikProvider } from 'formik';

// utils
import { generateTimeList as timeOptions } from 'utils/date';
import { i18n as I18n } from 'utils/i18n';
import {
  getSlotsForSpace,
  slotHasError,
  getBookingTotal,
  getConflicts,
  getMaxDate,
  isClinical,
} from 'utils/booking';
import Field from '../fields/field';

import { AuthContext } from '../../state/auth';
import { SearchContext } from '../../state/search';

// components
import AddToBasketButton from '../addToBasketButton';
import AddToBasketModal from '../addToBasketModal';
import BookingTotal from '../bookingTotal';
import DatepickerField from '../fields/datepickerField';
import ModalDialog from '../modal';
import SelectList from '../fields/selectList';
import ServiceSelect from '../fields/serviceSelect';
import SpaceTimeSlots from '../spaceTimeSlots';
import BookingHostForm from '../bookingHostForm';
import HostSelect from '../hostSelect';
import SlotsField from '../fields/slotsField';
import RecurrenceOptions from '../recurrenceOptions';

// hooks
import useAvailability from '../../hooks/useAvailability';
import useBookingHosts from '../../hooks/useBookingHosts';
import useBookingItems from '../../hooks/useBookingItems';
import useBookingSlots from '../../hooks/useBookingSlots';
import useClientServices from '../../hooks/useClientServices';
import useVenue from '../../hooks/useVenue';
import useVenueOpeningTimes from '../../hooks/useVenueOpeningTimes';

import useFormikBooking from '../../hooks/useFormikBooking';
import useIsMobile from '../../hooks/useIsMobile';
import useUpdateEffect from '../../hooks/useUpdateEffect';

interface Props {
  initialValues?: object;
}

export const slotNonEmpty = ({ startTime, endTime }) =>
  !!startTime && !!endTime;

const QuickBook: FC<Props> = ({ initialValues }, forwardedRef: Ref) => {
  const quickBookRef = useRef({});

  const router = useRouter();

  const { loggedIn, accountApproved } = useContext(AuthContext);
  const {
    filters: { date: searchDate },
  } = useContext(SearchContext);
  const [showBookingHostForm, toggleBookingHostForm] = useState(false);
  const [addToBasketOpen, setAddToBasketOpen] = useState(false);
  const isMobile = useIsMobile();

  const {
    data: services = [],
    bookableServices = [],
    loading: servicesLoading,
  } = useClientServices();

  const onSubmit = async () => setAddToBasketOpen(true);

  const {
    formik,
    initialising,
    generateBookingItems,
    clearAll: defaultClearAll,
    combinedSlots,
  } = useFormikBooking(
    true,
    { initialValues, enableReinitialize: addToBasketOpen, onSubmit },
    true,
    {
      services,
    }
  );

  const { values, setFieldValue, handleSubmit, isSubmitting } = formik;

  const { spaceIds, venueId } = values;

  const updatesEnabled = !initialising;

  const { data: venue = {}, loading: venueLoading } = useVenue(venueId, true);
  const { spaces: venueSpaces = [] } = venue;

  const spaces = useMemo(
    () => venueSpaces.filter(({ id }) => spaceIds.includes(id)),
    [venueSpaces, spaceIds]
  );

  const slots = useMemo(() => values.slots.filter(slotNonEmpty), [
    values.slots,
  ]);

  const onCheckRecurrence = clashesDetected => {
    if (clashesDetected) {
      if (!isMobile) router.push(I18n.t('navigation.advanced_booking.url'));
      return;
    }

    setFieldValue('bookingItems', generateBookingItems());
  };

  const clearAll = () =>
    defaultClearAll({ startTime: searchDate || undefined });

  useEffect(() => {
    if (searchDate) {
      setFieldValue('date', moment(searchDate).startOf('day').toDate());
    }
  }, [setFieldValue, searchDate]);

  useEffect(() => {
    if (values.date === undefined) {
      setFieldValue('date', moment().startOf('day').toDate());
    }
  }, [setFieldValue, values.date]);

  const deselectSpace = ({ id: spaceId }) =>
    spaceIds.length === 1
      ? clearAll()
      : setFieldValue(
          'spaceIds',
          spaceIds.filter(id => id !== spaceId)
        );

  const [
    { startTime: firstStartTime, endTime: firstEndTime } = {},
  ] = values.slots;

  useEffect(() => {
    if (!firstStartTime) return;

    const minEndTime = moment(firstStartTime, 'HH:mm').add(1, 'hour');

    if (!firstEndTime || moment(firstEndTime, 'HH:mm').isBefore(minEndTime))
      setFieldValue('slots.0.endTime', minEndTime.format('HH:mm'));
  }, [setFieldValue, firstStartTime, firstEndTime]);

  const clinical = useMemo(() => isClinical(spaces), [spaces]);

  useUpdateEffect(
    () => {
      setFieldValue('slots', []);
    },
    [clinical, setFieldValue],
    updatesEnabled && !venueLoading
  );

  const maxDate = useMemo(
    () =>
      getMaxDate(
        moment().toDate(),
        services.find(({ id }) => Number(id) === Number(values.service))
      ),
    [services, values.service]
  );

  const {
    data: { openingTime, closingTime },
    loading: openingTimesLoading,
  } = useVenueOpeningTimes(values.date, venueId);

  const lastBookingTimeForVenue = useMemo(
    () => moment(closingTime, 'HH:mm').subtract(1, 'hour').format('HH:mm'),
    [closingTime]
  );

  const startTime = openingTime;
  const endTime = closingTime;

  const { data: availability, loading: availabilityLoading } = useAvailability(
    values.date,
    values.service,
    spaces,
    combinedSlots,
    values.recurrence,
    undefined,
    values.id ? Number(values.id) : undefined
  );

  const bookingTotal = useMemo(() => getBookingTotal(availability, spaces), [
    availability,
    spaces,
  ]);

  const clashCount = useMemo(() => getConflicts(availability).length, [
    availability,
  ]);

  const hasBookingConflicts =
    values.recurrence.enabled === values.recurrence.checked && clashCount > 0;

  const addToBasketButtonIsDisabled =
    !slots.length ||
    !values.host ||
    !values.host.value ||
    !values.service ||
    hasBookingConflicts ||
    (values.recurrence.enabled ? !values.recurrence.checked : false) ||
    isSubmitting;

  const { data: bookingSlots } = useBookingSlots(values.date, venueId);
  const { data: bookingItems } = useBookingItems(values.date, spaceIds);

  const { data: bookingHosts, add: addBookingHost } = useBookingHosts();

  const hostFormSubmit = async host => {
    const { id: value, label } = await addBookingHost(host);
    setFieldValue('host', { label, value });
    toggleBookingHostForm(false);
  };

  const renderAdvancedBookingLink = () => {
    // if mobile and conflicts exist, we show a warning that advanced booking not available
    if (isMobile && hasBookingConflicts) {
      return (
        <div className="quick-book__clash-alert quick-book__clash-alert--faq">
          <p className="quick-book__clash-alert-text">
            {I18n.t('quick_book.timeslot_widget.conflict_faq_warning')}{' '}
            <Link href={I18n.t('navigation.faq.url')}>
              <a className="quick-book__clash-alert-link">
                {I18n.t('quick_book.timeslot_widget.conflict_faq_warning_link')}
              </a>
            </Link>
          </p>
        </div>
      );
    }

    // if mobile or conflicts exist, hide advanced booking link
    if (isMobile || hasBookingConflicts) {
      return null;
    }

    // otherwise show the advanced booking link
    return (
      <div className="quick-book__link">
        <Link href={I18n.t('navigation.advanced_booking.url')}>
          <a className="button button--text">
            {I18n.t('quick_book.go_to_advanced_booking')}
          </a>
        </Link>
      </div>
    );
  };

  const setRefs = useCallback(
    ref => {
      // eslint-disable-next-line no-param-reassign
      if (forwardedRef) forwardedRef.current = ref;
      quickBookRef.current = ref;
    },
    [forwardedRef]
  );

  return (
    <FormikProvider value={formik}>
      <>
        <div
          className="block block--with-border"
          id="quickBook"
          data-cy="bookingWidget"
          ref={setRefs}
        >
          <div className="block__inner">
            <div className="block__title">
              <h3 className="block__title-heading" data-testid="title">
                {I18n.t('quick_book.default_title')}
              </h3>

              {!!spaces.length && (
                <div className="block__title-action">
                  <button
                    className="button button--text"
                    data-testid="clearAll"
                    onClick={clearAll}
                  >
                    {I18n.t('shared.clear_all')}
                  </button>
                </div>
              )}
            </div>

            {loggedIn && (
              <>
                {!accountApproved && (
                  <p data-testid="description">
                    {I18n.t('quick_book.account_unapproved_message')}
                  </p>
                )}

                {accountApproved && !!spaces.length && (
                  <>
                    <div>
                      <Field
                        name="service"
                        as={ServiceSelect}
                        setValueProp="onChange"
                        services={bookableServices}
                        clinical={clinical}
                        loading={servicesLoading}
                        autoSelect={updatesEnabled}
                      />

                      <div data-cy="datepicker">
                        <Field
                          name="date"
                          as={DatepickerField}
                          setValueProp="onChange"
                          maxDate={maxDate}
                          dataCy="bookingWidgetDatePicker"
                        />
                      </div>

                      {!clinical && (
                        <div className="field-multicol">
                          <Field
                            name="slots.0.startTime"
                            as={SelectList}
                            setValueProp="onChange"
                            label={I18n.t('shared.start_time')}
                            placeholder={I18n.t('shared.start_time')}
                            options={timeOptions(
                              startTime,
                              lastBookingTimeForVenue
                            )}
                            disabled={!values.service && !values.date}
                            loading={openingTimesLoading}
                            testid="selectStartTime"
                          />

                          <Field
                            name="slots.0.endTime"
                            as={SelectList}
                            setValueProp="onChange"
                            label={I18n.t('shared.end_time')}
                            placeholder={I18n.t('shared.end_time')}
                            options={timeOptions(
                              firstStartTime
                                ? moment(firstStartTime, 'HH:mm').format(
                                    'HH:mm'
                                  )
                                : startTime,
                              endTime,
                              true
                            )}
                            disabled={!firstStartTime}
                            loading={openingTimesLoading}
                            testid="selectEndTime"
                          />
                        </div>
                      )}
                    </div>

                    <Field
                      name="slots"
                      as={SlotsField}
                      setValueProp="onChange"
                      clinical={clinical}
                      date={values.date}
                      bookingItems={bookingItems}
                      slots={bookingSlots}
                      availability={availability}
                      venue={venue}
                      spaces={spaces}
                      disabled={!values.date || !values.service}
                      loading={availabilityLoading}
                      openingTime={openingTime}
                      startTime={startTime}
                      endTime={endTime}
                    />
                    {venue.id && spaces && spaces.length && (
                      <h4 className="quick-book__selected_venue">
                        <Link
                          href={`${I18n.t('navigation.venues.url')}/${
                            venue.id
                          }`}
                        >
                          {venue.name}
                        </Link>
                      </h4>
                    )}

                    <ul
                      className="space-list"
                      data-cy="rooms"
                      id="selected_spaces"
                    >
                      {spaces.map(space => {
                        const spaceSlots = slots.length
                          ? getSlotsForSpace(
                              space.id,
                              slots,
                              availability,
                              availabilityLoading
                            )
                          : [];

                        return (
                          <SpaceTimeSlots
                            key={space.id}
                            space={space}
                            slots={spaceSlots}
                            onRemove={() => deselectSpace(space)}
                            hasError={slotHasError(spaceSlots)}
                            hasRecurrence={values.recurrence.enabled}
                            availability={availability}
                          />
                        );
                      })}
                    </ul>

                    <Link href={I18n.t('navigation.search.url')}>
                      <a className="quick-book__add_room">
                        {I18n.t('quick_book.add_another_room')}
                      </a>
                    </Link>

                    <RecurrenceOptions
                      disabled={
                        hasBookingConflicts && !values.recurrence.enabled
                          ? true
                          : undefined
                      }
                      services={services}
                      updatesEnabled={updatesEnabled}
                      loading={availabilityLoading}
                      clashCount={clashCount}
                      className="recurrence__quickbook"
                      id="recurrence__quickbook"
                      onCheck={onCheckRecurrence}
                      maxDate={maxDate}
                    />

                    {renderAdvancedBookingLink()}

                    <BookingTotal total={bookingTotal} />

                    <div className="host-select">
                      <Field
                        name="host"
                        as={HostSelect}
                        setValueProp="onChange"
                        bookingHosts={bookingHosts}
                      />

                      <div className="quick-book__link">
                        <a
                          className="button button--text"
                          onClick={() => toggleBookingHostForm(true)}
                        >
                          {I18n.t('quick_book.add_new_host')}
                        </a>
                      </div>
                    </div>

                    <div className="block__submit">
                      <AddToBasketButton
                        type="submit"
                        onClick={handleSubmit}
                        conflict={hasBookingConflicts && !isMobile}
                        disabled={addToBasketButtonIsDisabled}
                        loading={isSubmitting}
                        data-testid="addToBasketButton"
                      />
                    </div>
                  </>
                )}

                {accountApproved && !spaces.length && (
                  <p className="no-margin" data-testid="description">
                    {I18n.t('quick_book.add_rooms_message')}
                  </p>
                )}
              </>
            )}
            {!loggedIn && (
              <>
                <p className="intro" data-testid="description">
                  {I18n.t('quick_book.register_message')}
                </p>
                <Link href={I18n.t('navigation.register.url')}>
                  <a
                    data-testid="registerLink"
                    className="button button--block button--primary"
                  >
                    {I18n.t('shared.register_now')}
                  </a>
                </Link>
                <p className="note">
                  {I18n.t('quick_book.login_message')}{' '}
                  <Link href={I18n.t('navigation.sign_in.url')}>
                    <a>{I18n.t('navigation.sign_in.text')}</a>
                  </Link>
                </p>
              </>
            )}
          </div>
        </div>

        <AddToBasketModal
          isOpen={addToBasketOpen}
          onClose={() => setAddToBasketOpen(false)}
        />

        <ModalDialog
          isOpen={showBookingHostForm}
          onClose={() => {
            toggleBookingHostForm(false);
          }}
          headerLabel={I18n.t('booking_hosts.modal.title')}
          primaryAction={
            <button
              type="submit"
              form="bookingHostForm"
              className="button button--secondary"
              data-testid="bookingHostFormButton"
            >
              {I18n.t('booking_hosts.form.button')}
            </button>
          }
        >
          <BookingHostForm
            onSubmit={hostFormSubmit}
            bookingHosts={bookingHosts}
          />
        </ModalDialog>
      </>
    </FormikProvider>
  );
};

export default forwardRef(QuickBook);
